import { schnorr } from '@noble/curves/secp256k1'
import { sha256 } from '@noble/hashes/sha256'
import { bytesToHex, hexToBytes } from '@noble/hashes/utils'

// Fallback TextEncoder if nostr-tools utils fails (it typically exports utf8Encoder)
const encoder = new TextEncoder()

export interface DelegationConditions {
    kind?: number
    since?: number
    until?: number
}

export const delegationService = {
    /**
     * Create a delegation token manually (NIP-26)
     * @param delegateePubkey The pubkey of the bot/user receiving permission
     * @param conditions Conditions (kind, since, until)
     * @param signerPrivateKey The private key (hex) of the delegator (Admin)
     * @returns The delegation token string "nostr:delegation:..."
     */
    createDelegation: (
        delegateePubkey: string,
        conditions: DelegationConditions,
        signerPrivateKey: string
    ): string => {
        try {
            // 1. Construct condition string
            // Format: "kind=1&created_at>1680000000&created_at<1690000000"
            const parts: string[] = []
            if (conditions.kind !== undefined) parts.push(`kind=${conditions.kind}`)
            if (conditions.since !== undefined) parts.push(`created_at>${conditions.since}`)
            if (conditions.until !== undefined) parts.push(`created_at<${conditions.until}`)

            const conditionString = parts.join('&')

            // 2. Derive delegator pubkey from private key
            const privKeyBytes = hexToBytes(signerPrivateKey)
            const delegatorPubkey = schnorr.getPublicKey(privKeyBytes)
            const delegatorPubkeyHex = bytesToHex(delegatorPubkey)

            // 3. Create message to sign
            // "nostr:delegation:<delegatee_pubkey>:<conditions>"
            const message = `nostr:delegation:${delegateePubkey}:${conditionString}`

            // 4. Hash the message
            const messageBytes = encoder.encode(message)
            const hash = sha256(messageBytes)

            // 5. Sign the hash
            const signature = schnorr.sign(hash, privKeyBytes)
            const signatureHex = bytesToHex(signature)

            // 6. Return the token string
            // "nostr:delegation:<delegator_pubkey>:<conditions>:<signature>"
            return `nostr:delegation:${delegatorPubkeyHex}:${conditionString}:${signatureHex}`

        } catch (error) {
            console.error('Delegation creation failed:', error)
            throw new Error('Delegation creation failed: ' + (error instanceof Error ? error.message : 'Unknown error'))
        }
    }
}
